/**
 * Le calendrier naïf de l'app — arithmétique de dates, validations et formats
 * courts — vit dans `./time.ts` et se lit d'ici : `@ergo/shared` n'expose que « . ».
 */
export * from './time.js';
/**
 * La météo du jour vit dans `./meteo.ts` et se lit d'ici de la même façon : les
 * emojis, les seuils du « prends la tenue » et le résumé d'une journée.
 */
export * from './meteo.js';
/** Les adresses email d'un patient — plusieurs possibles — vivent dans `./emails.ts`. */
export * from './emails.js';
export const APPOINTMENT_STATUSES = ['planned', 'done', 'cancelled', 'no_show'];
export const DEFAULT_APPOINTMENT_DURATION_MIN = 45;
/** A series repeats every `intervalWeeks` weeks; weekly unless told otherwise. */
export const DEFAULT_SERIES_INTERVAL_WEEKS = 1;
export const MIN_SERIES_INTERVAL_WEEKS = 1;
export const MAX_SERIES_INTERVAL_WEEKS = 4;
export const SCHOOL_YEAR_END_MONTH = 7;
export const SCHOOL_YEAR_END_DAY = 14;
/** A series started in August or later belongs to the school year ending the next July. */
const SCHOOL_YEAR_FIRST_MONTH = 8;
/**
 * Default end of a series: mid-July of the school year its start date falls in.
 * Shared so the client can show the date a blank "jusqu'au" field will produce.
 */
export function schoolYearEndDate(startDate) {
    const year = Number(startDate.slice(0, 4));
    const month = Number(startDate.slice(5, 7));
    const endYear = month >= SCHOOL_YEAR_FIRST_MONTH ? year + 1 : year;
    return `${String(endYear).padStart(4, '0')}-${String(SCHOOL_YEAR_END_MONTH).padStart(2, '0')}-${String(SCHOOL_YEAR_END_DAY).padStart(2, '0')}`;
}
// ---------------------------------------------------------------- patients
export const LOCATION_KINDS = ['address', 'visio'];
/**
 * La visio n’est pas un lieu qu’elle ajoute : chaque patient l’a d’office. Une séance
 * la demande par ce marqueur à la place d’un id de lieu, et le serveur retrouve — ou
 * crée — l’unique lieu `visio` du patient derrière.
 */
export const VISIO_LOCATION = 'visio';
/** Le libellé du lieu `visio` que le serveur crée. */
export const VISIO_LOCATION_LABEL = 'Visio';
// ---------------------------------------------------------------- paiements
/**
 * A sane ceiling on a single payment: a month of séances is a few hundred euros,
 * so anything past this is a typo — a comma turned into nothing.
 */
export const MAX_PAYMENT_EUR = 10000;
/**
 * Le signe d'un rendez-vous important, partout où une séance se nomme : la grille,
 * la journée, le pointage, et jusqu'au titre de la notification. Jamais de rouge —
 * l'étoile et le brun chaud suffisent (docs/redesign-plus-douce.md).
 */
export const IMPORTANT_MARK = '★';
/** Un intitulé nomme une séance, il ne la raconte pas : une ligne d’agenda, pas plus. */
export const MAX_APPOINTMENT_TITLE_LENGTH = 60;
/**
 * Le nom d’une séance, partout où elle se nomme : l’agenda, le pointage, la fiche
 * patient, et jusqu’au titre de la notification poussée. L’étoile d’abord,
 * l’intitulé ensuite, le patient toujours en dernier — « ★ ESS – André ». Le gras
 * et le brun chaud viennent du CSS ; ici se fabrique le mot, une seule fois pour le
 * client comme pour le serveur.
 */
export function titleOf(appointment) {
    const mark = appointment.important ? `${IMPORTANT_MARK} ` : '';
    const named = appointment.title ? `${appointment.title} – ` : '';
    return `${mark}${named}${appointment.patientName}`;
}
/**
 * Ce qu'un encaissement de `baseCents` doit au cabinet, au centime. Le taux passe en
 * points de base (12,5 % → 1250) pour que l'arithmétique reste entière : un flottant
 * y perdrait un centime de temps en temps, et les lignes ne feraient plus le total.
 * Arrondi au centime supérieur à la demie.
 */
export function retrocessionCents(baseCents, ratePct) {
    const basisPoints = Math.round(ratePct * 100);
    if (basisPoints <= 0 || baseCents <= 0)
        return 0;
    return Math.floor((baseCents * basisPoints + 5000) / 10000);
}
// ---------------------------------------------------------------- settings
/** Minutes after the planned end before the end-of-session prompt goes out. */
export const DEFAULT_PROMPT_DELAY_MIN = 5;
export const MIN_PROMPT_DELAY_MIN = 0;
export const MAX_PROMPT_DELAY_MIN = 120;
/** Her hourly rate in euros: what the monthly report bills each séance at. */
export const DEFAULT_HOURLY_RATE_EUR = 56;
/**
 * Le forfait de la séance de 45 minutes, en euros. C'est sa grille réelle : 45
 * minutes se paient d'un prix, toute autre durée se calcule au taux horaire.
 */
export const DEFAULT_RATE_45_EUR = 46;
export const MAX_HOURLY_RATE_EUR = 500;
/**
 * La part des règlements des patients du cabinet reversée à la titulaire, en
 * pourcent. Zéro par défaut : sans taux, la rétrocession n'existe nulle part.
 */
export const DEFAULT_RETROCESSION_RATE_PCT = 0;
export const MAX_RETROCESSION_RATE_PCT = 100;
/** Le temps qu'elle veut garder entre une séance et un rendez-vous perso. */
export const DEFAULT_PERSO_MARGIN_MIN = 30;
export const MIN_PERSO_MARGIN_MIN = 0;
export const MAX_PERSO_MARGIN_MIN = 180;
/**
 * L'intensité des fêtes (docs/redesign-plus-douce.md) : « joueuse » lance neuf cœurs
 * et les pétales, « douce » en garde quatre et rien qui tombe. Le défaut est joueuse —
 * c'est le ton de l'écran ; « douce » est là pour les jours où elle n'en veut pas.
 */
export const CELEBRATION_INTENSITIES = ['douce', 'joueuse'];
export const DEFAULT_CELEBRATIONS = 'joueuse';
/** Nothing can be issued while the mandatory mention is missing. */
export function practitionerIsComplete(practitioner) {
    return practitioner.name.trim() !== '';
}
/**
 * Les rendez-vous perso qui tombent sur la séance, ou à moins de `marginMin` d'elle.
 * Les journées entières sont écartées : elles se lisent en bandeau, et signaler que
 * chaque séance « chevauche Vacances » ne rendrait service à personne.
 */
export function persoConflicts(slot, events, marginMin) {
    const from = slot.leavesAt ?? slot.startsAt;
    const to = addWallClockMinutes(slot.startsAt, slot.durationMin);
    return events
        .filter((event) => !event.allDay)
        .flatMap((event) => {
        if (event.startsAt < to && event.endsAt > from)
            return [{ event, overlaps: true }];
        const gapMin = event.startsAt >= to
            ? wallClockMinutesBetween(to, event.startsAt)
            : wallClockMinutesBetween(event.endsAt, from);
        return gapMin <= marginMin ? [{ event, overlaps: false }] : [];
    })
        .sort((a, b) => a.event.startsAt.localeCompare(b.event.startsAt));
}
/**
 * Arithmétique de mur, sur des chaînes naïves : les deux dates passent par le même
 * calendrier UTC, donc aucun changement d'heure ne s'y glisse (cf. DESIGN.md).
 */
function wallClockMs(dateTime) {
    return Date.UTC(Number(dateTime.slice(0, 4)), Number(dateTime.slice(5, 7)) - 1, Number(dateTime.slice(8, 10)), Number(dateTime.slice(11, 13)), Number(dateTime.slice(14, 16)));
}
function wallClockMinutesBetween(from, to) {
    return (wallClockMs(to) - wallClockMs(from)) / 60_000;
}
function addWallClockMinutes(dateTime, minutes) {
    return new Date(wallClockMs(dateTime) + minutes * 60_000).toISOString().slice(0, 16);
}
// --------------------------------------------------- vacances scolaires
/**
 * Les trois zones du calendrier scolaire métropolitain. Toulouse est en zone C —
 * c'est le défaut, et à peu près la seule qui la concerne ; les deux autres sont là
 * pour le jour où elle déménagerait, pas pour être choisies.
 */
export const SCHOOL_ZONES = ['A', 'B', 'C'];
export const DEFAULT_SCHOOL_ZONE = 'C';
/**
 * La période de vacances qui couvre ce jour-là, ou null. Début inclus, fin exclue :
 * le jour de la rentrée n'est plus des vacances. Les périodes ne se recouvrent pas ;
 * si jamais elles le faisaient, la première dans l'ordre donné gagne.
 */
export function holidayOn(date, holidays) {
    return holidays.find((holiday) => holiday.start <= date && date < holiday.end) ?? null;
}
// ---------------------------------------------------- factures
/** Sous qui elle exerce, tant qu'elle n'en dit pas autrement. */
export const DEFAULT_INVOICE_COLLABORATION_LINE = 'Collaboratrice de Suzie François';
/** Le titre imprimé sous son nom, tant qu'elle n'en dit pas d'autre. */
export const DEFAULT_PRACTITIONER_TITLE = 'Ergothérapeute DE libérale';
/** Le délai de paiement imprimé sur la facture, tant qu'elle n'en dit pas d'autre. */
export const DEFAULT_INVOICE_PAYMENT_TERMS = '30 jours à compter de la réalisation de la prestation.';
/**
 * Un IBAN a l'allure d'un IBAN : 15 à 34 caractères alphanumériques, espaces mis à
 * part. C'est la seule règle commune à tous les pays — la longueur exacte et la clé
 * de contrôle sont nationales, et les vérifier ici ne servirait qu'à refuser un
 * compte étranger parfaitement valide. Rendu compact et en majuscules, ou null.
 */
export function normalizedIban(value) {
    const compact = value.replace(/\s+/g, '').toUpperCase();
    return /^[A-Z0-9]{15,34}$/.test(compact) ? compact : null;
}
/** « FR7640618803820004087364634 » → « FR76 4061 8803 8200 0408 7364 634 ». */
export function formatIban(value) {
    return (value.replace(/\s+/g, '').toUpperCase().match(/.{1,4}/g) ?? []).join(' ');
}
/** Ce que dit le bas d'une facture sur ses tarifs, tant qu'elle n'en dit pas d'autre. */
export const DEFAULT_INVOICE_TARIFF_LINE = 'Tarifs au 1er septembre 2024 : 56 euros l’heure et 46 euros les 3/4 h à domicile.';
/**
 * The mention every note carries. Exact wording — art. 261-4-1° is the exemption a
 * paramedical profession falls under, *not* the 293 B franchise (docs/juridique.md §1).
 */
export const TVA_MENTION = 'TVA non applicable — exonération art. 261-4-1° du CGI';
/** What a line of the décompte is called; a visio séance says so. */
export const SEANCE_LABEL = 'Séance d’ergothérapie';
export const VISIO_SEANCE_LABEL = 'Séance d’ergothérapie en visio';
/** Une séance offerte : elle figure au décompte, à 0,00 €, et se dit comme telle. */
export const FREE_SEANCE_LABEL = 'Séance offerte';
/**
 * Le numéro de la première facture émise par l'app : elle reprend la suite de celles
 * qu'elle écrivait à la main, dont la dernière portait le 463.
 */
export const FIRST_INVOICE_SEQUENCE = 464;
/**
 * `FAC-AAAAMM-N` : le mois de rédaction, puis un compteur absolu — continu d'un mois
 * et d'une année à l'autre, jamais remis à zéro, jamais complété de zéros.
 */
export function noteNumber(issuedOn, sequence) {
    return `FAC-${issuedOn.slice(0, 4)}${issuedOn.slice(5, 7)}-${sequence}`;
}
// ------------------------------------------------------- feature requests
/** How long a single wish may be; more is a conversation, not a text box. */
export const MAX_FEATURE_REQUEST_LENGTH = 2000;
/** Notification action ids the service worker turns into an outcome PATCH. */
export const PUSH_ACTION_NO_SHOW = 'no_show';
export const PUSH_ACTION_45 = '45';
export const PUSH_ACTION_60 = '60';
/** Fixed padding on every bike estimate: locking up, ringing, finding the right door. */
export const BIKE_BUFFER_MIN = 5;
/**
 * Ce qu'il faut retrancher au début d'une séance pour savoir quand partir : la
 * course plus le battement. Null quand la course est inconnue, ou nulle — les deux
 * bouts sont alors la même porte, et il n'y a pas de départ à annoncer. Partagé
 * pour que la timeline du client et le flux iCal du serveur disent la même heure.
 */
export function departureLeadMin(bikeMinutes) {
    return bikeMinutes === null || bikeMinutes === 0 ? null : bikeMinutes + BIKE_BUFFER_MIN;
}
/**
 * The id the séance under edit wears in a preview: it may not exist yet, and the
 * legs still have to name the stop they lead to. No real row can collide with it.
 */
export const CANDIDATE_APPOINTMENT_ID = -1;
/* ---------- L'email qui porte la facture ---------- */
export const DEFAULT_INVOICE_EMAIL_SUBJECT = 'Votre facture {numero}';
export const DEFAULT_INVOICE_EMAIL_BODY = 'Bonjour,\n\nVous trouverez ci-jointe votre facture n° {numero}, établie le {date}.\n\n{praticienne}';
/** Les champs que l'email peut citer ; tout le reste du texte est à elle. */
export const INVOICE_EMAIL_PLACEHOLDERS = [
    'numero',
    'date',
    'mois',
    'patient',
    'prenom',
    'praticienne',
];
/** Remplace chaque {champ} connu ; un champ inconnu reste tel quel, visible, plutôt que d'être avalé. */
export function renderEmailTemplate(template, values) {
    return template.replace(/\{(\w+)\}/g, (whole, key) => key in values ? values[key] : whole);
}
//# sourceMappingURL=index.js.map